import * as React from 'react';

import type { Unit } from '@rematter/frontend-lib-api-client';

import { useScaleWeightsSubscription } from '../_graphql/subscriptionScaleWeights';

const WEIGHT_STABILITY_TOLERANCE_LB = 1; // 1 lb
const REQUIRED_STABLE_READINGS = 3;

// Simple weight stability checker - considers weight stable if it hasn't changed significantly
const useIsWeightStable = (weight: number | null): boolean => {
  const [isStable, setIsStable] = React.useState(false);
  const previousWeightRef = React.useRef<number | null>(null);
  const stableCountRef = React.useRef(0);

  React.useEffect(() => {
    if (weight === null) {
      setIsStable(false);
      stableCountRef.current = 0;
      return;
    }

    const previousWeight = previousWeightRef.current;

    if (
      previousWeight !== null &&
      Math.abs(weight - previousWeight) < WEIGHT_STABILITY_TOLERANCE_LB
    ) {
      stableCountRef.current += 1;
      // Consider stable after consecutive similar readings
      if (stableCountRef.current >= REQUIRED_STABLE_READINGS) {
        setIsStable(true);
      }
    } else {
      stableCountRef.current = 0;
      setIsStable(false);
    }

    previousWeightRef.current = weight;
  }, [weight]);

  return isStable;
};

export const useScaleReading = (scaleId?: string) => {
  const { data } = useScaleWeightsSubscription(
    { scaleId: scaleId },
    { skip: !scaleId },
  );

  const weight = React.useMemo(() => {
    if (!data?.scaleWeightsUpdated?.grossWeight) {
      return null;
    }

    const grossWeight = data.scaleWeightsUpdated.grossWeight;
    if (Number.isNaN(grossWeight)) {
      return null;
    }

    return grossWeight;
  }, [data]);

  const isStable = useIsWeightStable(weight);

  return { weight, isStable };
};

// Event emitter pattern for take weight events
type TakeWeightPayload = {
  weight: number;
  isStable: boolean;
  unitOfMeasurement: Unit;
  snapshotFileId?: string;
};
type TakeWeightListener = (payload: TakeWeightPayload) => void;

let takeWeightListeners: TakeWeightListener[] = [];

export const useTakeWeightListener = (
  listener: TakeWeightListener,
  dependencies: React.DependencyList,
) => {
  const listenerRef = React.useRef(listener);
  listenerRef.current = listener;

  React.useEffect(() => {
    const newListener = listenerRef.current;
    takeWeightListeners.push(newListener);
    return () => {
      takeWeightListeners = takeWeightListeners.filter(
        (it) => it !== newListener,
      );
    };
  }, dependencies);
};

export const useTakeWeightEmitter = () => {
  const emitTakeWeightEvent = React.useCallback(
    (payload: TakeWeightPayload) => {
      takeWeightListeners.forEach((it) => setTimeout(() => it(payload)));
    },
    [],
  );

  return {
    emitTakeWeightEvent,
  };
};
